import { Box, makeStyles, useMediaQuery } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import {
    ContainerMui,
    ColorHex,
    Hr,
    Typography,
    Variant,
    Police,
    BorderRadiusMui
} from '../../../../styles';
import { TexteAvecInfoBulle } from '../../../../components';
import { FrequenceDureeTerme } from '../../../../hooks';

const useStyles = makeStyles(() => ({
    titre: {
        fontSize: '20px !important'
    }
}));

interface IRepartitionDonutProps {
    amortissementRestant: { annee: string; mois: string };
    montantCapital: number;
    montantInteret: number;
    montantAssurance: number;
    valeurDureeTerme: number;
    frequenceDureeTerme: FrequenceDureeTerme;
    onTooltipActive: (label: string) => void;
}

const TOTAL_HEIGHT = 416;

export const RepartitionDonut: React.FC<IRepartitionDonutProps> = ({
    amortissementRestant,
    montantCapital,
    montantInteret,
    montantAssurance,
    valeurDureeTerme,
    frequenceDureeTerme,
    onTooltipActive
}) => {
    const [t] = useTranslation('RepartitionDonut');
    const isMobile = useMediaQuery('(max-width:640px)');
    const isEcranMedium = useMediaQuery('(max-width:1440px)');
    const classes = useStyles();

    const renderTitre = (): string => {
        return (
            t('chartTitle_1') +
            t(
                'Date:' +
                    (frequenceDureeTerme === FrequenceDureeTerme.MOIS
                        ? 'monthsOffer'
                        : 'yearsOffer'),
                {
                    value: valeurDureeTerme
                }
            ) +
            t('chartTitle_2')
        );
    };

    const RenderAmortissementRestant = () => {
        const formattedAnnee = t('Date:years', {
            value: amortissementRestant?.annee
        });
        const formattedMois = t('Date:months', {
            value: amortissementRestant?.mois
        });

        return (
            <>
                <Hr backgroundColor={ColorHex.graphite} />
                <Box margin="1.5rem 1rem">
                    <TexteAvecInfoBulle
                        isCentreeDansContainer
                        isLabelEtIconeCentres={!isMobile}
                        label={
                            <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="nowrap"
                            >
                                <Typography
                                    variant={Variant.p}
                                    hideMarginBottom
                                >
                                    <strong>
                                        {formattedAnnee}
                                        {Number(amortissementRestant?.mois) >
                                            0 && t('and') + ' ' + formattedMois}
                                    </strong>
                                    {t('amortissementRestant')}
                                </Typography>
                            </Box>
                        }
                        labelInfoBulle={t('tooltipAmortissementRestant')}
                        labelHorsEcranInfoBulle={t('tooltipHorsEcran')}
                        iconColorCustom={ColorHex.lightGraphite5}
                        onToolTipActive={() => {
                            onTooltipActive('Amortissement restant');
                        }}
                    />
                </Box>
            </>
        );
    };

    return (
        <ContainerMui
            dataTestId={'bloc_repartition_paiements'}
            borderRadius={BorderRadiusMui.sm}
            border={2}
            borderColor={ColorHex.graphite}
            hidePadding
            maxWidth={isMobile ? '100%' : '71%'}
            minHeight={TOTAL_HEIGHT + 'px'}
            spacerDistance={6}
        >
            <Box display="block" position="relative">
                <Box padding="1rem 1rem 0 1rem">
                    <TexteAvecInfoBulle
                        label={renderTitre()}
                        labelVariant={Variant.h3}
                        labelPolice={Police.bold}
                        labelClass={classes.titre}
                        labelInfoBulle={t('tooltipTitle')}
                        labelHorsEcranInfoBulle={renderTitre()}
                        iconColorCustom={ColorHex.lightGraphite5}
                        isLabelEtIconeCentres={!isMobile}
                        onToolTipActive={() =>
                            onTooltipActive('Répartition des paiements')
                        }
                    />
                </Box>

                <Box display="flex" justifyContent="center" alignItems="center">
                    <HighchartsReact
                        highcharts={Highcharts}
                        containerProps={{
                            style: {
                                height: '300px',
                                width: '100%'
                            }
                        }}
                        options={{
                            chart: {
                                plotBackgroundColor: null,
                                plotBorderWidth: null,
                                plotShadow: false,
                                type: 'pie'
                            },
                            title: {
                                text: undefined
                            },
                            tooltip: {
                                formatter: function () {
                                    return (
                                        //@ts-ignore
                                        this.key +
                                        ': ' +
                                        '<b>' +
                                        t('Number:currency', {
                                            //@ts-ignore
                                            value: this.y
                                        }) +
                                        '</b>'
                                    );
                                }
                            },
                            credits: {
                                enabled: false
                            },
                            accessibility: {
                                point: {
                                    valueSuffix: '$'
                                }
                            },
                            legend: {
                                layout: 'horizontal',
                                align: 'left',
                                verticalAlign: 'top',
                                floating: true,
                                backgroundColor: '#FFFFFF',
                                navigation: {
                                    enabled: false
                                },
                                itemStyle: {
                                    fontSize: '14px',
                                    fontWeight: 400
                                },
                                y: -7,
                                x: -1
                            },
                            plotOptions: {
                                pie: {
                                    cursor: 'pointer',
                                    size: isMobile ? 180 : 200,
                                    dataLabels: {
                                        enabled: false
                                    },
                                    center: isEcranMedium
                                        ? ['50%', '55%']
                                        : ['50%', '60%'],
                                    showInLegend: true,
                                    point: {
                                        events: {
                                            legendItemClick: function () {
                                                return false;
                                            }
                                        }
                                    }
                                }
                            },
                            series: [
                                {
                                    name: t('seriesTitle'),
                                    data: [
                                        {
                                            name: t('seriesCapital'),
                                            y: montantCapital,
                                            color: '#008c53'
                                        },
                                        {
                                            name: t('seriesInteret'),
                                            y: montantInteret,
                                            color: '#8ec9a9'
                                        },
                                        montantAssurance !== 0
                                            ? {
                                                  name: t('seriesAssurance'),
                                                  y: montantAssurance,
                                                  color: '#cce7dc'
                                              }
                                            : undefined
                                    ]
                                }
                            ]
                        }}
                    />
                </Box>
                <RenderAmortissementRestant />
            </Box>
        </ContainerMui>
    );
};
